import styled from "@emotion/styled";
import { Flex } from "@radix-ui/themes";
import type React from "react";
import { useRef, useState } from "react";

import type { BN } from "@utils/BN";

import { theme } from "../themes/themes";

import { BigNumberInput } from "./BigNumberInput";
import { Icon } from "./Icon";

type Props = {
  label?: string;
  value: BN;
  onChange?: (v: BN) => void;
  placeholder?: string;
  rightLabel?: string;
  onRightLabelClick?: () => void;
  rightAction?: string;
  rightActionDisabled?: boolean;
  onRightActionClick?: () => void;
  clearAction?: string;
  clearActionDisabled?: boolean;
  onClearActionClick?: () => void;
  error?: boolean;
  disabled?: boolean;
  variant?: "primary" | "secondary";
  decimalScale?: number;
  displayDecimals?: number;
  inputTestId?: string;
  rightActionTestId?: string;
  clearActionTestId?: string;
  errorTestId?: string;
  rightLabelTestId?: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">;

export const OrderInput: React.FC<Props> = ({
  label,
  value,
  onChange,
  placeholder = "0.00",
  rightLabel,
  onRightLabelClick,
  rightAction,
  rightActionDisabled = true,
  onRightActionClick,
  clearAction,
  clearActionDisabled = true,
  onClearActionClick,
  error,
  disabled,
  variant = "primary",
  decimalScale = 2,
  displayDecimals,
  rightActionTestId,
  clearActionTestId,
  inputTestId,
  errorTestId,
  rightLabelTestId,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null!);
  const rightLabelRef = useRef<HTMLDivElement>(null);
  const rightActionRef = useRef<HTMLButtonElement>(null);
  const isActive = isFocused && !error;

  const handleWrapperClick = (e: React.MouseEvent) => {
    if (rightActionRef.current?.contains(e.target as Node) || rightLabelRef.current?.contains(e.target as Node)) {
      return;
    }

    inputRef.current?.focus();
  };

  const handleInputChange = (value: BN) => {
    onChange?.(value);
  };

  const handleRightActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (rightActionDisabled) return;
    onRightActionClick?.();
  };

  const handleClearActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (clearActionDisabled) return;
    onClearActionClick?.();
  };

  return (
    <Wrapper
      {...rest}
      {...(isActive && { "data-active": true })}
      {...(error && { "data-error": true })}
      {...(error && errorTestId && { "data-testid": errorTestId })}
      disabled={disabled}
      variant={variant}
      onClick={handleWrapperClick}
    >
      <Flex direction="column" gap="4px">
        {label && (
          <LabelRow>
            <Label>{label}</Label>
            <Actions>
              {rightAction && (
                <RightAction
                  {...(rightActionTestId && { "data-testid": rightActionTestId })}
                  ref={rightActionRef}
                  disabled={rightActionDisabled}
                  onClick={handleRightActionClick}
                >
                  {rightAction}
                </RightAction>
              )}
              {clearAction && (
                <ClearAction
                  {...(clearActionTestId && { "data-testid": clearActionTestId })}
                  disabled={clearActionDisabled}
                  onClick={handleClearActionClick}
                >
                  {clearAction}
                </ClearAction>
              )}
            </Actions>
          </LabelRow>
        )}
        <StyledInput
          inputTestId={inputTestId}
          decimals={decimalScale}
          disabled={disabled}
          displayDecimals={displayDecimals}
          haserror={error}
          hasvalue={!!value}
          inputRef={inputRef}
          placeholder={placeholder}
          value={value}
          onBlur={() => setIsFocused(false)}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
        />
      </Flex>
      {rightLabel && (
        <RightLabelContainer
          {...(rightLabelTestId && { "data-testid": rightLabelTestId })}
          align="center"
          data-pointer={onRightLabelClick ? "pointer" : "default"}
          gap="4px"
          onClick={onRightLabelClick}
        >
          <RightLabel ref={rightLabelRef}>{rightLabel}</RightLabel>
          {onRightLabelClick && <Icon color="gray-11" icon="ri-arrow-left-right-line" size="16px" />}
        </RightLabelContainer>
      )}
    </Wrapper>
  );
};

const Wrapper = styled(Flex)<{
  disabled?: boolean;
  variant?: "primary" | "secondary";
}>`
  flex: 1 1 0%;
  min-width: 0;
  align-items: center;
  justify-content: space-between;

  height: 62px;
  border-radius: 12px;
  border: 1px solid
    ${({ ...props }) =>
      (props as any)["data-error"]
        ? theme.color("red-strong")
        : (props as any)["data-active"]
          ? theme.color("accent-7")
          : theme.color("gray-5")};

  background: ${({ variant }) => (variant === "primary" ? theme.color("gray-4") : theme.color("gray-5"))};

  padding: 12px;

  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "text")};
`;

const LabelRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const Actions = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const Label = styled.span`
  color: ${theme.color("gray-11")};
  ${theme.font.body12};
  white-space: nowrap;
`;

const StyledInput = styled(BigNumberInput)<{ haserror?: boolean; hasvalue?: boolean }>`
  background: transparent;
  border: none;
  outline: none;
  ${theme.font.body14};
  min-height: 24px;
  line-height: 24px;
  color: ${({ haserror, hasvalue }) =>
    haserror ? theme.color("red-strong") : hasvalue ? theme.color("accent-contrast") : theme.color("gray-8")};

  &::placeholder {
    color: ${theme.color("gray-8")};
    ${theme.font.body14};
  }

  caret-color: ${theme.color("accent-9")};
  padding: 0;

  width: 100%;
`;

const RightAction = styled.button<{ disabled: boolean }>`
  background: none;
  border: none;
  color: ${({ disabled }) => (disabled ? theme.color("gray-8") : theme.color("accent-9"))};
  ${theme.font.body12};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  min-height: 24px;
  padding: 0 2px;
`;

const ClearAction = styled.button<{ disabled: boolean }>`
  background: none;
  border: none;
  color: ${({ disabled }) => (disabled ? theme.color("gray-8") : theme.color("accent-9"))};
  ${theme.font.body12};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  min-height: 24px;
  padding: 0 2px;
`;

const RightLabel = styled.span`
  color: ${theme.color("gray-11")};
  ${theme.font.body14};
`;

const RightLabelContainer = styled(Flex)`
  &[data-pointer="pointer"] {
    cursor: pointer;

    & > i {
      cursor: inherit !important;
    }
  }
`;
